import { parse24, to24, type Meridiem } from "./timeOfDay";

/**
 * Hour / minute / AM-PM picker for one end of the date window.
 *
 * Three plain `<select>`s rather than `<input type="time">`: the native control
 * renders 12h or 24h according to the BROWSER locale, so anyone not on a US
 * locale would never see AM/PM. These always do.
 *
 * `value` is 24-hour "HH:mm" (the wire format); the AM/PM split lives only here.
 */

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0-59

const SELECT_CLASS =
  "h-9 rounded-[10px] border border-[var(--color-black-2)] bg-[#fcfcfc] px-2 text-[13px] text-[#505e59] " +
  "outline-none focus:border-[#008069] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";

interface TimeOfDayFieldProps {
  label: string;
  /** 24-hour "HH:mm". */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Used when `value` is blank so a partial edit still produces a full time. */
  fallback: string;
}

export default function TimeOfDayField({
  label,
  value,
  onChange,
  disabled = false,
  fallback,
}: TimeOfDayFieldProps) {
  const current = parse24(value) ?? parse24(fallback)!;

  const emit = (patch: Partial<typeof current>) =>
    onChange(to24({ ...current, ...patch }));

  return (
    <div className="flex items-center gap-2">
      <span className="w-10 shrink-0 text-[12px] text-[#697571]">{label}</span>

      <select
        aria-label={`${label} hour`}
        className={SELECT_CLASS}
        value={current.hour}
        disabled={disabled}
        onChange={(e) => emit({ hour: Number(e.target.value) })}
      >
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>

      <span className="text-[13px] text-[#697571]">:</span>

      <select
        aria-label={`${label} minute`}
        className={SELECT_CLASS}
        value={current.minute}
        disabled={disabled}
        onChange={(e) => emit({ minute: Number(e.target.value) })}
      >
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, "0")}
          </option>
        ))}
      </select>

      <select
        aria-label={`${label} AM or PM`}
        className={SELECT_CLASS}
        value={current.meridiem}
        disabled={disabled}
        onChange={(e) => emit({ meridiem: e.target.value as Meridiem })}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
